import { Link as RouterLink } from "react-router-dom";
import { addMonths, addWeeks, addYears, format } from "date-fns";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Link,
  MenuItem,
  TextField,
  useTheme,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Section from "components/Section";
import SectionHeader from "components/SectionHeader";
import OutlinedCard from "components/OutlinedCard";
import { useGetBookingsQuery } from "app/api/bookings";
import DataTable, { ColumnOptions } from "components/DataTable";
import { Booking } from "models/Booking";
import LoadingDisplay from "components/LoadingDisplay";
import { useGetAllOfficesQuery } from "app/api/offices";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { usePersistState } from "lib/hooks/usePersistState";
import CheckCircle from "@mui/icons-material/CheckCircle";

const columns: ColumnOptions<Booking> = [
  {
    key: "assigneeName",
    label: "Installatör",
    dense: true,
    renderCell: (row) => (
      <Link component={RouterLink} to={row.assignee ? `/users/view/${row.assignee.id}` : "#"}>
        {row.assignee?.userName || "Okänd"}
      </Link>
    ),
  },
  {
    key: "orderNumber",
    label: "Order",
    dense: true,
    renderCell: (row) => (
      <Link component={RouterLink} to={`/orders/view/${row.orderNumber}`}>
        {row.orderNumber}
      </Link>
    ),
  },
  {
    key: "title",
    label: "Titel",
  },
  {
    key: "date",
    label: "Datum",
    align: "right",
    dense: true,
    sortBy: (row) => row.startTime,
    renderCell: (row) => format(row.startTime, "yyyy-MM-dd"),
  },
  {
    key: "startTime",
    label: "Tid",
    align: "right",
    dense: true,
    renderCell: (row) => `${format(row.startTime, "HH:mm")} - ${format(row.endTime, "HH:mm")}`,
  },
  {
    key: "isDone",
    label: "Rapp.",
    align: "right",
    dense: true,
    renderCell: (row) =>
      row.isDone ? (
        <CheckCircle color="success" fontSize="small" sx={{ verticalAlign: "top" }} />
      ) : null,
  },
];

const PeriodOptions = [
  { key: "today", label: "Idag", startDate: new Date() },
  { key: "lastWeek", label: "Senaste veckan", startDate: addWeeks(new Date(), -1) },
  { key: "lastMonth", label: "Senaste månaden", startDate: addMonths(new Date(), -1) },
  { key: "lastYear", label: "Senaste året", startDate: addYears(new Date(), -1) },
] as const;

type PeriodKey = (typeof PeriodOptions)[number]["key"];

function getPeriodStartDate(period: PeriodKey): Date {
  const periodOption = PeriodOptions.find((option) => option.key === period);
  return periodOption ? periodOption.startDate : new Date();
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
}

const AllBookingsList: React.FC = () => {
  const theme = useTheme();

  const [showContent, setShowContent] = usePersistState("Bookings.showContent", false);
  const [selectedPeriod, setSelectedPeriod] = usePersistState<PeriodKey>(
    "Bookings.selectedPeriod",
    PeriodOptions[0].key
  );
  const [selectedOffice, setSelectedOffice] = usePersistState<number | null>(
    "Bookings.selectedOffice",
    null
  );
  const [showCompleted, setShowCompleted] = usePersistState("Bookings.showCompleted", false);

  const [sortBy, setSortBy] = usePersistState<(typeof columns)[number]["key"]>(
    "Bookings.sortBy",
    "assigneeName"
  );
  const [sortDir, setSortDir] = usePersistState<"asc" | "desc">("Bookings.sortDir", "asc");

  const { data: offices } = useGetAllOfficesQuery();
  const { data: bookings, isFetching } = useGetBookingsQuery({
    startDate: formatDate(getPeriodStartDate(selectedPeriod)),
    endDate: formatDate(new Date()),
    officeId: selectedOffice,
    excludeDone: !showCompleted,
    excludeInternal: true,
  });

  return (
    <Section>
      <SectionHeader title="Bokningar" />

      <OutlinedCard>
        <Collapse in={showContent}>
          <Box display="flex" gap={1} pb={1}>
            <TextField
              select
              label="Period"
              variant="filled"
              size="small"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as PeriodKey)}
              sx={{ minWidth: 200, flexGrow: 0 }}
            >
              {PeriodOptions.map((col) => (
                <MenuItem key={col.key} value={col.key}>
                  {col.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Kontor"
              variant="filled"
              size="small"
              value={selectedOffice ?? ""}
              onChange={(e) => setSelectedOffice(e.target.value ? Number(e.target.value) : null)}
              sx={{ minWidth: 200, flexGrow: 0 }}
            >
              <MenuItem value="" sx={{ color: "text.disabled" }}>
                Alla
              </MenuItem>
              {offices?.map((office) => (
                <MenuItem key={office.id} value={office.id}>
                  {office.name}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              label="Visa rapporterade"
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
              }
            />
          </Box>

          {isFetching ? (
            <LoadingDisplay message="Hämtar bokningar" />
          ) : (
            <DataTable
              data={bookings ?? []}
              columns={columns}
              getKey={(row) => row.id}
              noBottomBorder
              sort={{ sortBy, sortDir }}
              onSortChange={(newSortBy, newSortDir) => {
                setSortBy(newSortBy);
                setSortDir(newSortDir);
              }}
              emptyMessage="Inga bokningar matchar sökningen"
              sx={{ mb: 1 }}
              rowStyles={(row) => ({
                ...(row.isDone && {
                  backgroundColor: `${alpha(theme.palette.success.light, 0.1)}`,
                }),
              })}
            />
          )}
        </Collapse>

        <Button
          variant="text"
          fullWidth
          startIcon={showContent ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowContent(!showContent)}
          sx={{ justifyContent: "flex-end" }}
        >
          {showContent ? "Dölj bokningar" : "Visa bokningar"}
        </Button>
      </OutlinedCard>
    </Section>
  );
};

export default AllBookingsList;
